import { useMsal, useAccount } from '@azure/msal-react';
import {
  AuthError,
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { loginRequest, apiRequest, silentApiRequest, authGroups } from '@/config/authConfig';
import {
  clearStaleMsalInteraction,
  clearInteractiveRetry,
  interactiveRetryPending,
  markInteractiveRetry,
} from '@/auth/msalRecovery';
import {
  getLocalToken,
  clearLocalToken,
  subscribeLocalSession,
  isLocalTokenExpired,
  decodeLocalToken,
} from '@/auth/localSession';
import {
  ensureAuthMe,
  getAuthMeSnapshot,
  resetAuthMe,
  subscribeAuthMe,
} from '@/auth/authMeStore';
import type { UserInfo, UserPermissions } from '@ip/shared-types';

// Singleton promise to deduplicate concurrent force-refresh calls
let inflightRefresh: Promise<string | null> | null = null;

// Set while an interactive redirect navigation has been HANDED to the browser
// but the document has not been replaced yet (the AAD page can take seconds to
// commit). The latch itself — "the once-only fallback already fired" — lives in
// msalRecovery.ts so the SessionExpiredModal can reset it too.
let redirectInProgress = false;

/**
 * True between "acquireTokenRedirect started navigating" and the document
 * actually unloading (2026-09-07).
 *
 * useApi consults this before flipping `sessionExpired`: the request that
 * TRIGGERED the fallback would otherwise raise the "Sesja wygasła" modal over
 * a page that is already on its way to AAD. Worse than cosmetic — the modal's
 * button calls clearStaleMsalInteraction(), which would delete the PKCE
 * verifier and request params of the redirect currently in flight, so the
 * response coming back from AAD would find no matching cached request and the
 * sign-in would fail.
 */
export function isAuthRedirectInProgress(): boolean {
  return redirectInProgress;
}

/**
 * BrowserAuthError codes that mean "the hidden-iframe silent flow could not
 * complete" — NOT "the user's credentials are bad" (2026-09-07 incident).
 *
 * Before this, only InteractionRequiredAuthError triggered the interactive
 * fallback, so a bridge timeout fell through to `return null`, which useApi
 * turns into setSessionExpired(true) → the "Sesja wygasła" modal, with no way
 * back. All of these are recoverable by exactly the same move the modal's
 * button makes: an interactive redirect. Codes verified against
 * @azure/msal-browser 5.21 BrowserAuthErrorCodes.
 */
const INTERACTIVE_ERROR_CODES = new Set<string>([
  'timed_out', // bridge never answered (subError redirect_bridge_timeout)
  'redirect_bridge_empty_response',
  'iframe_closed_prematurely',
  'empty_window_error',
  'block_iframe_reload', // SPA (not the bridge) loaded in the silent iframe
]);

/**
 * AADSTS50011 = "the redirect URI in the request does not match the ones
 * configured for the application".
 *
 * silentApiRequest carries `redirectUri: <origin>/redirect.html`, and MSAL
 * forwards a request-level redirectUri onto the REFRESH-TOKEN grant too, not
 * only the hidden-iframe /authorize call (verified in msal-browser 5.21:
 * SilentRefreshClient.acquireToken → msal-common RefreshTokenClient
 * .createTokenRequestBody → addRedirectUri). So until /redirect.html is
 * registered as an SPA redirect URI in Entra, a tenant that validates
 * redirect_uri on refresh-token redemption answers AADSTS50011 — as a
 * ServerError, which is neither InteractionRequiredAuthError nor
 * BrowserAuthError and would therefore fall through to `return null` and pin
 * the session-expired modal with no way out.
 *
 * Matching on the AADSTS code (rather than on an error class) is deliberate:
 * it makes the deploy safe in BOTH orders — before the Entra registration
 * lands, users keep working via the interactive redirect; after it lands,
 * this branch simply never fires.
 */
const REDIRECT_URI_MISMATCH = /AADSTS50011/;

/** Does this failure warrant an interactive (redirect) retry? */
function needsInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  if (error instanceof BrowserAuthError && INTERACTIVE_ERROR_CODES.has(error.errorCode)) {
    return true;
  }
  return error instanceof AuthError && REDIRECT_URI_MISMATCH.test(error.errorMessage ?? '');
}

/**
 * EXACTLY ONE interactive fallback per tab session: redirect once; if we come
 * back and silent acquisition still fails, give up and let the
 * SessionExpiredModal handle it. Never loop.
 *
 * Returns after having started a navigation (or after the redirect itself
 * failed) — either way the caller's request cannot be satisfied now.
 */
async function runInteractiveFallbackOnce(
  instance: IPublicClientApplication,
  account: AccountInfo,
  inProgress: string,
): Promise<void> {
  if (interactiveRetryPending() || redirectInProgress || inProgress !== 'none') {
    console.info('[Auth] Interactive auth required — returning null (modal will handle)');
    return;
  }
  console.info('[Auth] Falling back to acquireTokenRedirect (once)');
  redirectInProgress = true;
  markInteractiveRetry();
  try {
    await instance.acquireTokenRedirect({ ...apiRequest, account });
  } catch (redirectError) {
    // 2026-09-07: `interaction_in_progress` can ONLY be thrown here, never by
    // acquireTokenSilent — the silent path runs preflightCheck alone
    // (blockNonBrowserEnvironment / blockReloadInHiddenIframes /
    // blockAcquireTokenInPopups / blockAPICallsBeforeInitialize) and never
    // reads `msal.interaction.status`; only setInteractionInProgress(true,
    // SIGNIN) inside acquireTokenRedirect/loginRedirect does, and it has no
    // allowOverride escape hatch (unlike acquireTokenPopup). So the stale-flag
    // recovery HAS to live in this catch: clear the flag, redirect once more.
    // Guarded on inProgress === 'none' so we never cancel an interaction the
    // user is genuinely in the middle of.
    const stalePendingInteraction =
      redirectError instanceof BrowserAuthError &&
      redirectError.errorCode === 'interaction_in_progress' &&
      inProgress === 'none';

    if (stalePendingInteraction) {
      console.warn('[Auth] Stale interaction_in_progress — clearing MSAL state and retrying once');
      clearStaleMsalInteraction();
      try {
        await instance.acquireTokenRedirect({ ...apiRequest, account });
        return;
      } catch (retryError) {
        console.error('[Auth] acquireTokenRedirect failed after clearing state:', retryError);
      }
    } else {
      console.error('[Auth] acquireTokenRedirect failed:', redirectError);
    }

    // No navigation happened, so there is nothing for the once-only latch to
    // guard against — leaving it armed would silently disable the automatic
    // fallback for the next 5 minutes and force the user through the modal.
    redirectInProgress = false;
    clearInteractiveRetry();
  }
}

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] ?? undefined);

  // Local (email + password) session — when a local JWT exists it is the
  // active session and MSAL is never consulted for tokens.
  const localToken = useSyncExternalStore(subscribeLocalSession, getLocalToken);
  const isLocalSession = localToken !== null;

  const authMe = useSyncExternalStore(subscribeAuthMe, getAuthMeSnapshot);

  const isAuthenticated = isLocalSession || accounts.length > 0;
  const isLoading = !isLocalSession && inProgress !== 'none';

  // True once we KNOW the user's real permission set — i.e. MSAL account
  // restoration has settled (or this is a local session, which has none) AND
  // /auth/me has either resolved or given up (falling back to legacy group
  // parsing) for the CURRENT session. Consumers that gate rendering on
  // isSuperAdmin / permissions (admin routes, RootRedirect) must wait for
  // this before evaluating the gate — otherwise a hard navigation/reload
  // reads transient "not yet loaded" values (isSuperAdmin=false,
  // permissions=null) and bounces a legitimate user away before the real
  // answer arrives. Unauthenticated sessions are always "ready" (nothing to
  // wait for) so the login page isn't blocked.

  // Get access token for API calls (local JWT or MSAL access token)
  const getAccessToken = useCallback(async (options?: { forceRefresh?: boolean }): Promise<string | null> => {
    // Local session: the JWT is not refreshable — return it while valid,
    // null once expired (callers surface the session-expired modal).
    const local = getLocalToken();
    if (local !== null) {
      if (isLocalTokenExpired(local)) {
        console.info('[Auth] Local token expired');
        return null;
      }
      return local;
    }

    if (!account) {
      console.warn('[Auth] No account available for token acquisition');
      return null;
    }

    // Deduplicate concurrent force-refresh calls
    if (options?.forceRefresh && inflightRefresh) {
      console.debug('[Auth] Joining existing force-refresh request');
      return inflightRefresh;
    }

    const doAcquire = async (): Promise<string | null> => {
      try {
        // silentApiRequest (not apiRequest): the hidden renewal iframe must
        // load the MSAL redirect-bridge page, never the SPA (2026-09-07).
        const response = await instance.acquireTokenSilent({
          ...silentApiRequest,
          account,
          forceRefresh: options?.forceRefresh ?? false,
        });
        clearInteractiveRetry();
        // A successful acquisition ends any redirect we were waiting on —
        // leaving the module latch set would block the NEXT recovery for the
        // life of the tab (2026-09-07).
        redirectInProgress = false;
        console.debug('[Auth] Token acquired, expires:', response.expiresOn?.toISOString());
        return response.accessToken;
      } catch (error) {
        // NOTE: acquireTokenSilent can never throw `interaction_in_progress`
        // (its preflight does not read `msal.interaction.status`) — the stale
        // flag surfaces from acquireTokenRedirect and is recovered inside
        // runInteractiveFallbackOnce.
        if (needsInteraction(error)) {
          await runInteractiveFallbackOnce(instance, account, inProgress);
          return null;
        }
        console.error('[Auth] Token acquisition failed:', error);
        return null;
      }
    };

    if (options?.forceRefresh) {
      inflightRefresh = doAcquire().finally(() => { inflightRefresh = null; });
      return inflightRefresh;
    }

    return doAcquire();
  }, [instance, account, inProgress]);

  // Fetch /auth/me once per session (either provider) — deduped in the store.
  const sessionKey = isLocalSession
    ? `local:${decodeLocalToken(localToken)?.sub ?? localToken.slice(-16)}`
    : account
      ? `entra:${account.homeAccountId}`
      : null;

  useEffect(() => {
    if (sessionKey) {
      ensureAuthMe(sessionKey, getAccessToken);
    }
  }, [sessionKey, getAccessToken]);

  const permissions: UserPermissions | null =
    authMe.status === 'loaded' && authMe.sessionKey === sessionKey
      ? authMe.data?.permissions ?? null
      : null;

  // See isAuthReady doc comment above isLoading. Not authenticated => ready
  // (nothing to wait for). Authenticated => wait for MSAL restoration to
  // settle AND /auth/me for the CURRENT session to reach a terminal state.
  const authMeSettledForSession =
    authMe.sessionKey === sessionKey && (authMe.status === 'loaded' || authMe.status === 'error');
  const isAuthReady = !isAuthenticated || (!isLoading && sessionKey !== null && authMeSettledForSession);

  // ---- Legacy Entra group parsing (fallback until /auth/me responds) ----
  const userGroups = useMemo((): string[] => {
    if (!account?.idTokenClaims) return [];
    const claims = account.idTokenClaims as Record<string, unknown>;

    // Groups can be in 'groups' claim or '_claim_names' for overage scenario
    if (Array.isArray(claims.groups)) {
      return claims.groups as string[];
    }

    // Handle group overage - groups might need to be fetched from Graph API
    if (claims._claim_names && typeof claims._claim_names === 'object') {
      console.warn('Group overage detected. Groups need to be fetched from Graph API.');
      return [];
    }

    return [];
  }, [account]);

  const legacyIsAdmin = useMemo(() => {
    if (!authGroups.adminGroupId) return false;
    return userGroups.includes(authGroups.adminGroupId);
  }, [userGroups]);

  const legacyAllowedCompanies = useMemo((): string[] => {
    if (legacyIsAdmin) return []; // Admin has access to all, no specific list needed

    return userGroups
      .map((groupId) => authGroups.companyGroups.get(groupId))
      .filter((nip): nip is string => !!nip);
  }, [userGroups, legacyIsAdmin]);

  // ---- Permission surface: /auth/me when loaded, legacy fallback otherwise ----
  const isSuperAdmin = permissions?.isSuperAdmin ?? (isLocalSession ? false : legacyIsAdmin);
  const isAdmin = isSuperAdmin;
  const allowedCompanies = useMemo((): string[] => {
    if (permissions) {
      // Preserve legacy semantics: admins get an empty list (= all companies)
      return permissions.isSuperAdmin ? [] : Object.keys(permissions.companies);
    }
    return isLocalSession ? [] : legacyAllowedCompanies;
  }, [permissions, isLocalSession, legacyAllowedCompanies]);

  // Get user info
  const userInfo = useMemo((): UserInfo | null => {
    if (isLocalSession) {
      const me = authMe.sessionKey === sessionKey ? authMe.data?.user : null;
      const payload = decodeLocalToken(localToken);
      const email = me?.email ?? payload?.email ?? '';
      return {
        id: me?.id ?? payload?.sub ?? '',
        email,
        name: me?.displayName || email,
        groups: [],
        isAdmin,
        allowedCompanies,
      };
    }

    if (!account) return null;

    return {
      id: account.localAccountId,
      email: account.username,
      name: account.name || account.username,
      groups: userGroups,
      isAdmin,
      allowedCompanies,
    };
  }, [isLocalSession, localToken, authMe, sessionKey, account, userGroups, isAdmin, allowedCompanies]);

  // Login function (Entra) — full-page redirect; MSAL v5 popup flows require
  // a COOP redirect-bridge page, redirect flows do not.
  const login = useCallback(async () => {
    clearInteractiveRetry();
    // 2026-09-07: loginRedirect calls setInteractionInProgress(true, SIGNIN)
    // WITHOUT allowOverride, so a leftover `msal.interaction.status` from an
    // abandoned redirect makes this throw interaction_in_progress for the
    // life of the tab. Every deliberate sign-in starts from a clean slate —
    // this covers the SessionExpiredModal button AND the login page.
    clearStaleMsalInteraction();
    redirectInProgress = false;
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  // Logout clears BOTH session types
  const logout = useCallback(async () => {
    resetAuthMe();
    if (getLocalToken() !== null) {
      clearLocalToken();
      window.location.assign('/');
      return;
    }
    try {
      await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance]);

  return {
    isAuthenticated,
    isLoading,
    isAuthReady,
    isLocalSession,
    userInfo,
    isAdmin,
    isSuperAdmin,
    permissions,
    allowedCompanies,
    login,
    logout,
    getAccessToken,
  };
}
